import {
  type AppItemsData,
  type AppItemsDataRequestParams,
  itemsDataRequestBody,
  parseAppItemsData,
} from "@/features/app-studio/api/appItemsApi";
import { asTabFilterField, type TabFilterField } from "@/features/app-studio/api/appTabFiltersApi";
import type { TabFilterValue } from "@/features/app-studio/model/appTabFilters";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

function filtersPath(appId: number, tabId: number | null): string {
  const base = `/v1/published-apps/${appId}/filters`;
  return tabId === null ? base : `${base}?tab_id=${tabId}`;
}

/** The saved filter fields (with resolved dropdown values) for a published app's tab. */
export async function listPublishedTabFilters(
  appId: number,
  tabId: number | null,
): Promise<TabFilterField[]> {
  const res = await apiFetch(filtersPath(appId, tabId), { method: "GET" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data)) {
    throw new Error("Unexpected response shape");
  }
  return data.map(asTabFilterField);
}

/**
 * Re-run a published app's tab widgets with the viewer's ephemeral filter selection applied.
 * Mirrors the builder's filtered refresh but on the consumer route. `tabId` null runs every widget.
 */
export async function fetchPublishedAppItemsDataFiltered(
  appId: number,
  params: AppItemsDataRequestParams,
): Promise<AppItemsData> {
  const res = await apiFetch(`/v1/published-apps/${appId}/items/data`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: itemsDataRequestBody(params),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return parseAppItemsData(await res.json());
}

/** Save the viewer's per-user value for a filter field (overrides the builder default for them). */
export async function savePublishedTabFilterPreference(
  appId: number,
  filterId: number,
  value: TabFilterValue,
): Promise<TabFilterField> {
  const res = await apiFetch(`/v1/published-apps/${appId}/filters/${filterId}/preference`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(value),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  return asTabFilterField(await res.json());
}

/** Clear the viewer's saved values for a tab, resetting to the app-configured defaults. */
export async function clearPublishedTabFilterPreferences(
  appId: number,
  tabId: number | null,
): Promise<void> {
  const base = `/v1/published-apps/${appId}/filters/preferences`;
  const path = tabId === null ? base : `${base}?tab_id=${tabId}`;
  const res = await apiFetch(path, { method: "DELETE" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}
